import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  // Ajout : demande d'un lien de reinitialisation par e-mail. N'enleve rien au
  // parcours existant, le formulaire de connexion reste identique.
  const [modeOubli, setModeOubli] = useState(false);
  const [emailOubli, setEmailOubli] = useState('');
  const [envoiEnCours, setEnvoiEnCours] = useState(false);

  const demanderLien = async (e) => {
    e.preventDefault();
    if (!emailOubli.trim()) return;
    setEnvoiEnCours(true);
    try {
      await api.forgotPassword(emailOubli.trim());
    } catch (_) {
      // Volontairement silencieux : la reponse du serveur est neutre, qu'un
      // compte existe ou non. Afficher une erreur reviendrait a reveler
      // quelles adresses possedent un compte.
    } finally {
      setEnvoiEnCours(false);
      setModeOubli(false);
      setEmailOubli('');
      toast({
        title: 'Demande envoyée',
        description: "Si cette adresse correspond à un compte, un e-mail vient d'être envoyé. Pensez à vérifier les indésirables.",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      toast({
        title: 'Connexion réussie',
        description: 'Bienvenue sur votre tableau de bord énergétique',
      });
    } else {
      toast({
        title: 'Erreur de connexion',
        description: result.error,
        variant: 'destructive',
      });
    }

    setLoading(false);
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden">
      {/* Image d'arrière-plan floutée — bg-cover pour remplir toute la largeur sans laisser
          de bandes vides. La hauteur sera centrée et la partie haute/basse de l'image
          peut être croppée selon le ratio de la viewport. */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-slate-900 blur-sm"
        style={{ backgroundImage: "url('/login-bg.png')" }}
        aria-hidden="true"
      />
      {/* Overlay sombre pour garantir le contraste du formulaire au-dessus */}
      <div className="absolute inset-0 bg-slate-950/60" aria-hidden="true" />

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img
              src="/electra-icon.png"
              alt="ELECTRA"
              className="h-32 object-contain rounded-2xl drop-shadow-2xl"
            />
          </div>
          <p className="text-slate-300">Solution de Monitoring Énergétique</p>
        </div>

        <Card className="bg-slate-800/70 border-slate-700/50 backdrop-blur-md shadow-2xl">
          <CardHeader>
            <CardTitle className="text-white">Connexion</CardTitle>
            <CardDescription className="text-slate-400">
              Accédez à votre tableau de bord
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre.email@entreprise.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={loading}
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </Button>
            </form>

            {/* Ajout : acces au libre-service mot de passe */}
            {!modeOubli ? (
              <button
                type="button"
                onClick={() => setModeOubli(true)}
                className="w-full mt-4 text-slate-400 hover:text-blue-400 text-sm transition-colors"
              >
                Mot de passe oublié ?
              </button>
            ) : (
              <form onSubmit={demanderLien} className="mt-4 pt-4 border-t border-slate-700 space-y-3">
                <p className="text-slate-400 text-sm">
                  Indiquez votre adresse e-mail. Vous recevrez un lien pour choisir
                  vous-même un nouveau mot de passe.
                </p>
                <Input
                  type="email"
                  placeholder="votre.adresse@exemple.com"
                  value={emailOubli}
                  onChange={(e) => setEmailOubli(e.target.value)}
                  required
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                />
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={envoiEnCours}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {envoiEnCours ? 'Envoi...' : 'Recevoir le lien'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => { setModeOubli(false); setEmailOubli(''); }}
                    className="bg-slate-700 hover:bg-slate-600 text-slate-200"
                  >
                    Annuler
                  </Button>
                </div>
              </form>
            )}

          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;